import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/layout/PageHeader';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Power } from 'lucide-react';

interface ApiProviderRow {
  id: string;
  name: string;
  baseUrl: string;
  apiKey: string; // masked
  modelName: string | null;
  isEnabled: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function AdminApiProvidersPage() {
  const { t } = useTranslation(['adminApiProviders', 'common']);
  const [providers, setProviders] = useState<ApiProviderRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [baseUrlInput, setBaseUrlInput] = useState('');
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [modelNameInput, setModelNameInput] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const res = await fetch('/api/admin/api-providers', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setProviders(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch API providers:', error);
      toast.error(t('adminApiProviders:errors.load'));
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setNameInput('');
    setBaseUrlInput('');
    setApiKeyInput('');
    setModelNameInput('');
    setShowModal(true);
  };

  const openEditModal = (provider: ApiProviderRow) => {
    setEditingId(provider.id);
    setNameInput(provider.name);
    setBaseUrlInput(provider.baseUrl);
    setApiKeyInput('');
    setModelNameInput(provider.modelName || '');
    setShowModal(true);
  };

  const saveProvider = async () => {
    if (!nameInput.trim() || !baseUrlInput.trim()) {
      toast.error(t('adminApiProviders:errors.nameBaseRequired'));
      return;
    }
    if (!editingId && !apiKeyInput.trim()) {
      toast.error(t('adminApiProviders:errors.apiKeyRequired'));
      return;
    }

    try {
      const url = editingId
        ? `/api/admin/api-providers/${editingId}`
        : '/api/admin/api-providers';
      const method = editingId ? 'PUT' : 'POST';

      const body: Record<string, string | null> = {
        name: nameInput.trim(),
        baseUrl: baseUrlInput.trim(),
        modelName: modelNameInput.trim() || null,
      };
      if (apiKeyInput.trim()) {
        body.apiKey = apiKeyInput.trim();
      }

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (res.ok) {
        toast.success(editingId ? t('adminApiProviders:messages.updated') : t('adminApiProviders:messages.created'));
        setShowModal(false);
        fetchProviders();
      } else {
        const data = await res.json();
        toast.error(data.error || t('adminApiProviders:errors.save'));
      }
    } catch (error) {
      console.error('Failed to save provider:', error);
      toast.error(t('adminApiProviders:errors.save'));
    }
  };

  const activateProvider = async (id: string) => {
    try {
      const res = await fetch(`/api/admin/api-providers/${id}/activate`, {
        method: 'POST',
        credentials: 'include',
      });

      if (res.ok) {
        toast.success(t('adminApiProviders:messages.setDefault'));
        fetchProviders();
      } else {
        const data = await res.json();
        toast.error(data.error || t('adminApiProviders:errors.activate'));
      }
    } catch (error) {
      console.error('Failed to activate provider:', error);
      toast.error(t('adminApiProviders:errors.activate'));
    }
  };

  const toggleEnabled = async (provider: ApiProviderRow) => {
    try {
      const res = await fetch(`/api/admin/api-providers/${provider.id}/toggle-enabled`, {
        method: 'POST',
        credentials: 'include',
      });

      if (res.ok) {
        toast.success(provider.isEnabled ? t('adminApiProviders:messages.disabled') : t('adminApiProviders:messages.enabled'));
        fetchProviders();
      } else {
        const data = await res.json();
        toast.error(data.error || t('adminApiProviders:errors.update'));
      }
    } catch (error) {
      console.error('Failed to toggle provider:', error);
      toast.error(t('adminApiProviders:errors.update'));
    }
  };

  const deleteProvider = async () => {
    if (!deleteId) return;

    try {
      const res = await fetch(`/api/admin/api-providers/${deleteId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (res.ok) {
        toast.success(t('adminApiProviders:messages.deleted'));
        setShowDeleteModal(false);
        setDeleteId(null);
        fetchProviders();
      } else {
        const data = await res.json();
        toast.error(data.error || t('adminApiProviders:errors.delete'));
      }
    } catch (error) {
      console.error('Failed to delete provider:', error);
      toast.error(t('adminApiProviders:errors.delete'));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('adminApiProviders:title')} description={t('adminApiProviders:description')} />
        <div className="space-y-2">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-14 rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader title={t('adminApiProviders:title')} description={t('adminApiProviders:description')} />
        <Button onClick={openCreateModal} size="sm">
          <Plus className="h-4 w-4 mr-1" />
          {t('adminApiProviders:actions.add')}
        </Button>
      </div>

      <div className="bg-muted rounded-lg p-4 text-sm text-muted-foreground">
        <p>{t('adminApiProviders:help')}</p>
      </div>

      {providers.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">{t('adminApiProviders:empty')}</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('adminApiProviders:table.name')}</TableHead>
                <TableHead>{t('adminApiProviders:table.model')}</TableHead>
                <TableHead>{t('adminApiProviders:table.baseUrl')}</TableHead>
                <TableHead>{t('adminApiProviders:table.apiKey')}</TableHead>
                <TableHead>{t('adminApiProviders:table.status')}</TableHead>
                <TableHead className="text-right">{t('adminApiProviders:table.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {providers.map((provider) => (
                <TableRow key={provider.id}>
                  <TableCell className="font-medium">{provider.name}</TableCell>
                  <TableCell>
                    {provider.modelName ? (
                      <code className="text-xs bg-muted px-2 py-0.5 rounded">{provider.modelName}</code>
                    ) : (
                      <span className="text-xs text-muted-foreground">{t('adminApiProviders:table.none')}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-2 py-0.5 rounded">{provider.baseUrl}</code>
                  </TableCell>
                  <TableCell>
                    <code className="text-xs text-muted-foreground">{provider.apiKey}</code>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant={provider.isEnabled ? 'default' : 'secondary'}>
                        {provider.isEnabled ? t('adminApiProviders:status.enabled') : t('adminApiProviders:status.disabled')}
                      </Badge>
                      {provider.isActive && (
                        <Badge variant="outline">{t('adminApiProviders:status.default')}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant={provider.isEnabled ? 'secondary' : 'outline'}
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => toggleEnabled(provider)}
                      >
                        <Power className="h-3.5 w-3.5 mr-1" />
                        {provider.isEnabled ? t('adminApiProviders:actions.disable') : t('adminApiProviders:actions.enable')}
                      </Button>
                      {!provider.isActive && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => activateProvider(provider.id)}
                        >
                          {t('adminApiProviders:actions.setDefault')}
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label={t('adminApiProviders:actions.edit')} onClick={() => openEditModal(provider)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      {!provider.isActive && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          aria-label={t('adminApiProviders:actions.delete')}
                          onClick={() => { setDeleteId(provider.id); setShowDeleteModal(true); }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? t('adminApiProviders:dialog.editTitle') : t('adminApiProviders:dialog.addTitle')}</DialogTitle>
            <DialogDescription>
              {editingId
                ? t('adminApiProviders:dialog.editDescription')
                : t('adminApiProviders:dialog.addDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="providerName">{t('adminApiProviders:form.name')}</Label>
              <Input
                id="providerName"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                placeholder={t('adminApiProviders:form.namePlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="providerModelName">{t('adminApiProviders:form.modelNames')}</Label>
              <Input
                id="providerModelName"
                value={modelNameInput}
                onChange={(e) => setModelNameInput(e.target.value)}
                placeholder={t('adminApiProviders:form.modelNamesPlaceholder')}
              />
              <p className="text-xs text-muted-foreground">
                {t('adminApiProviders:form.modelNamesHelp')}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="providerBaseUrl">{t('adminApiProviders:form.baseUrl')}</Label>
              <Input
                id="providerBaseUrl"
                value={baseUrlInput}
                onChange={(e) => setBaseUrlInput(e.target.value)}
                placeholder={t('adminApiProviders:form.baseUrlPlaceholder')}
              />
              <p className="text-xs text-muted-foreground">
                {t('adminApiProviders:form.baseUrlHelpPrefix')} <code>/v1/messages</code>{t('adminApiProviders:form.baseUrlHelpSuffix')}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="providerApiKey">{t('adminApiProviders:form.apiKey')}</Label>
              <Input
                id="providerApiKey"
                type="password"
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                placeholder={editingId ? t('adminApiProviders:form.apiKeyEditPlaceholder') : t('adminApiProviders:form.apiKeyPlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>{t('common:cancel')}</Button>
            <Button onClick={saveProvider}>{t('common:save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={showDeleteModal}
        onOpenChange={(open) => { setShowDeleteModal(open); if (!open) setDeleteId(null); }}
        title={t('adminApiProviders:deleteDialog.title')}
        description={t('adminApiProviders:deleteDialog.description')}
        confirmLabel={t('adminApiProviders:actions.delete')}
        cancelLabel={t('common:cancel')}
        variant="destructive"
        onConfirm={deleteProvider}
      />
    </div>
  );
}
